import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react';
import crmApi from '../utils/crmApi';
import { useCRM } from './CRMContext';

const FeatureRegistryContext = createContext(null);

export const FeatureRegistryProvider = ({ children }) => {
  const { token } = useCRM();
  const [features, setFeatures] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    if (!token) {
      setFeatures({});
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await crmApi.get('/config/features');
      if (res.data?.success && res.data?.data?.features) {
        setFeatures(res.data.data.features);
      } else {
        setFeatures({});
      }
    } catch (err) {
      setError(err);
      setFeatures({});
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const getFeatureStatus = useCallback((featureKey) => {
    if (!featureKey) return 'live';
    return features?.[featureKey]?.status || 'live';
  }, [features]);

  const isFeatureLive = useCallback((featureKey) => {
    const status = getFeatureStatus(featureKey);
    return status !== 'hidden';
  }, [getFeatureStatus]);

  const value = useMemo(() => ({
    features,
    loading,
    error,
    isFeatureLive,
    getFeatureStatus,
    refresh,
  }), [features, loading, error, isFeatureLive, getFeatureStatus, refresh]);

  return (
    <FeatureRegistryContext.Provider value={value}>
      {children}
    </FeatureRegistryContext.Provider>
  );
};

export const useFeatureRegistry = () => {
  const ctx = useContext(FeatureRegistryContext);
  if (!ctx) {
    throw new Error('useFeatureRegistry must be used within FeatureRegistryProvider');
  }
  return ctx;
};


